import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import Icon, { CaretRightOutlined, SettingOutlined } from '@ant-design/icons';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { Automation } from '../../types';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { iconForAutomationType } from '../../helpers/displayHelpers';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { AutomationPlanExecutionTooltipIcon } from '../planRestrictions/planRestrictionHelperComponents';
import { BetaBadge, UpdatedBadge } from './AppAutomationSettings';
import { InlineStepConfigDescription } from '@/components/common/InlineStepConfigDescription';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectIsWorkflowsCIStepEnabled,
    selectStepConfigDataFromStepGraphs,
} from '@/selectors/selectors';

export const automationIsEnabled = (automation: Automation) =>
    automation.enabled || Object.values(automation.configPerReleaseType ?? {}).some((override) => override.enabled);

interface AutomationRowProps {
    appId: string;
    // all automations must share a single AutomationType — one per step config
    automations: Automation[];
    isDisabledDueToPricingPlan: boolean;
    onSelectAutomationForSettings: (automationId: string) => void;
    hideBadges?: boolean;
}

export const AutomationRow: React.FC<AutomationRowProps> = (props: AutomationRowProps) => {
    const { appId, automations, isDisabledDueToPricingPlan, onSelectAutomationForSettings, hideBadges } = props;

    const stepGraphStepsMap = useSelector(
        (state: RootState) => selectStepConfigDataFromStepGraphs(state, appId),
        shallowEqual,
    );
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    const badgeAndSettings = (automation: Automation) => {
        const isEnabled = automationIsEnabled(automation);
        const showPlanWontExecuteIcon = isDisabledDueToPricingPlan && isEnabled;
        const tagShowsEnabled = isEnabled && !isDisabledDueToPricingPlan;

        return (
            <Space align="center" size={12}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        columnGap: showPlanWontExecuteIcon ? 12 : 0,
                    }}
                >
                    <AutomationPlanExecutionTooltipIcon visible={showPlanWontExecuteIcon} />
                    <Tag
                        color={runwayColors.white}
                        style={{
                            ...tagStyle,
                            fontSize: 10,
                            borderColor: tagShowsEnabled ? runwayColors.statusGreen : antGrey.grey6,
                            color: tagShowsEnabled ? runwayColors.statusGreen : antGrey.grey6,
                        }}
                    >
                        {tagShowsEnabled ? 'Enabled' : 'Disabled'}
                    </Tag>
                </span>
                <Button
                    ghost
                    icon={
                        <SettingOutlined
                            style={{
                                color: isDisabledDueToPricingPlan ? antGrey.grey6 : runwayColors.info,
                            }}
                        />
                    }
                    onClick={() => onSelectAutomationForSettings(automation.id)}
                />
            </Space>
        );
    };

    const automationRow = ({
        automation,
        isEnabled,
        shouldShowBadgeAndSettings,
    }: {
        automation: Automation;
        isEnabled: boolean;
        shouldShowBadgeAndSettings?: boolean;
    }) => {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                }}
            >
                <Space size={18} align="center">
                    <Icon
                        disabled={isDisabledDueToPricingPlan}
                        component={iconForAutomationType(automation.type)}
                        style={{
                            fontSize: 16,
                            color: isEnabled ? antGrey.grey7 : antGrey.grey5,
                            paddingTop: 4,
                        }}
                    />
                    <Text disabled={isDisabledDueToPricingPlan} type={isEnabled ? undefined : 'secondary'}>
                        {automation.name}
                    </Text>
                    <PlanRestrictedFeatureWrapper automationType={automation.type} requiresTooltip />
                    {!hideBadges && (
                        <div style={{ display: 'flex', gap: '1px' }}>
                            {automation.isBeta && <BetaBadge />}{' '}
                            {automation.isNew && (
                                <Tag
                                    color={runwayColors.primary}
                                    style={{
                                        ...tagStyle,
                                        fontSize: 10,
                                        color: 'white',
                                    }}
                                >
                                    New
                                </Tag>
                            )}{' '}
                            {automation.isUpdated && <UpdatedBadge />}
                        </div>
                    )}
                </Space>
                {shouldShowBadgeAndSettings && badgeAndSettings(automation)}
            </div>
        );
    };

    const isAnyAutomationEnabled = automations.some(automationIsEnabled);
    const hasMultipleAutomationsOfTheSameType = automations.length > 1;

    if (!hasMultipleAutomationsOfTheSameType) {
        return automationRow({
            automation: automations[0],
            isEnabled: isAnyAutomationEnabled,
            shouldShowBadgeAndSettings: true,
        });
    }

    return (
        <Collapse
            className="automations-collapsible"
            defaultActiveKey={[]}
            style={{
                flex: 1,
            }}
            ghost
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 14,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
        >
            <Collapse.Panel
                header={automationRow({
                    automation: automations[0],
                    isEnabled: isAnyAutomationEnabled,
                    shouldShowBadgeAndSettings: false,
                })}
                key={'1'}
            >
                <List
                    bordered
                    size="small"
                    dataSource={automations}
                    renderItem={(automation) => {
                        const stepGraphStep =
                            automation.stepConfigId ? stepGraphStepsMap[automation.stepConfigId] : null;
                        if (!stepGraphStep) {
                            return null;
                        }
                        return (
                            <List.Item key={automation.id}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 16,
                                        width: '100%',
                                    }}
                                >
                                    <InlineStepConfigDescription
                                        stepGraphStep={stepGraphStep}
                                        platform={platform}
                                        appStoreType={appStoreType}
                                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                    />
                                    {badgeAndSettings(automation)}
                                </div>
                            </List.Item>
                        );
                    }}
                />
            </Collapse.Panel>
        </Collapse>
    );
};
